import { useCallback, useEffect, useMemo, useRef } from "react"
import { useReactFlow } from "@xyflow/react"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  CommandDialog,
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command"
import { MODULE_REGISTRY, getModulesByCategory, getModuleDefinition } from "@/lib/registry/modules"
import { MODULE_ICONS } from "@/lib/registry/module-icons"
import { DATA_TYPE_INFO } from "@/lib/registry/data-types"
import { usePipelineStore } from "@/lib/stores/pipeline-store"
import { useModelCatalogStore } from "@/lib/stores/model-catalog-store"
import { PARAM_TYPE_TO_DATA_TYPE } from "@/lib/types/model-catalog"
import type { DataType, ModuleCategory, ModuleDefinition } from "@/lib/types/module"

const CATEGORY_LABELS: Record<ModuleCategory, string> = {
  input: "Input",
  generation: "Generation",
  transform: "Transform",
  output: "Output",
  utility: "Utility",
  control: "Control",
  agent: "Agent",
}

const CATEGORIES: ModuleCategory[] = ["agent", "input", "generation", "transform", "output", "utility", "control"]

/** Info about a connection that was dropped on empty canvas */
export interface PendingConnection {
  nodeId: string
  handleId: string
  handleType: "source" | "target"
  dataType: DataType
}

interface ModulePaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Position to place the new node. If not set, uses viewport center. */
  insertPosition?: { x: number; y: number } | null
  /** If set, palette filters by compatible type and auto-connects after creation */
  pendingConnection?: PendingConnection | null
}

/** Check if a module has a compatible handle for the pending connection */
function isModuleCompatible(mod: ModuleDefinition, pending: PendingConnection): boolean {
  if (pending.handleType === "source") {
    // Dragged from an output → need a module with a matching input
    if (mod.inputs.some((i) => i.handle && i.type === pending.dataType)) return true
    // For model-catalog nodes, inputs come from catalog params — check all available models
    if (mod.modelConfig) {
      const { models } = useModelCatalogStore.getState()
      return models.some(
        (m) =>
          m.categories.some((c) => mod.modelConfig!.categories.includes(c)) &&
          m.parameters_schema.params.some((p) => PARAM_TYPE_TO_DATA_TYPE[p.type] === pending.dataType)
      )
    }
    return false
  } else {
    // Dragged from an input → need a module with a matching output
    return mod.outputs.some((o) => o.handle && o.type === pending.dataType)
  }
}

/** Find the first compatible handle name on a module for auto-connect */
function findCompatibleHandle(mod: ModuleDefinition, pending: PendingConnection): string | null {
  if (pending.handleType === "source") {
    const input = mod.inputs.find((i) => i.handle && i.type === pending.dataType)
    if (input) return input.name
    // For model-catalog nodes, use default model's first matching param
    if (mod.modelConfig) {
      const defaultModel = useModelCatalogStore.getState().getDefaultForModule(mod.module)
      const param = defaultModel?.parameters_schema.params.find(
        (p) => PARAM_TYPE_TO_DATA_TYPE[p.type] === pending.dataType
      )
      return param?.name ?? null
    }
    return null
  } else {
    const output = mod.outputs.find((o) => o.handle && o.type === pending.dataType)
    return output?.name ?? null
  }
}

export function ModulePalette({ open, onOpenChange, insertPosition, pendingConnection }: ModulePaletteProps) {
  const addNode = usePipelineStore((s) => s.addNode)
  const selectNode = usePipelineStore((s) => s.selectNode)
  const onConnect = usePipelineStore((s) => s.onConnect)
  const { screenToFlowPosition } = useReactFlow()
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus the input when dialog opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [open])

  // Filter modules by pending connection compatibility
  const filteredModules = useMemo(() => {
    if (!pendingConnection) return null // null means show all
    return MODULE_REGISTRY.filter((mod) => isModuleCompatible(mod, pendingConnection))
  }, [pendingConnection])

  const handleSelect = useCallback(
    (moduleId: string) => {
      let position: { x: number; y: number }

      if (insertPosition) {
        position = insertPosition
      } else {
        // Place at center of current viewport
        position = screenToFlowPosition({
          x: window.innerWidth / 2,
          y: window.innerHeight / 2,
        })
        // Offset slightly so nodes don't stack exactly
        position.x += (Math.random() - 0.5) * 50
        position.y += (Math.random() - 0.5) * 50
      }

      const nodeId = addNode(moduleId, position)
      if (!nodeId) return

      // Auto-connect if we have a pending connection
      if (pendingConnection) {
        const newModuleDef = getModuleDefinition(moduleId)
        if (newModuleDef) {
          const compatibleHandle = findCompatibleHandle(newModuleDef, pendingConnection)
          if (compatibleHandle) {
            if (pendingConnection.handleType === "source") {
              // Dragged from output → new node's input
              onConnect({
                source: pendingConnection.nodeId,
                target: nodeId,
                sourceHandle: pendingConnection.handleId,
                targetHandle: compatibleHandle,
              })
            } else {
              // Dragged from input → new node's output
              onConnect({
                source: nodeId,
                target: pendingConnection.nodeId,
                sourceHandle: compatibleHandle,
                targetHandle: pendingConnection.handleId,
              })
            }
          }
        }
      }

      selectNode(nodeId)
      onOpenChange(false)
    },
    [addNode, selectNode, onConnect, onOpenChange, insertPosition, pendingConnection, screenToFlowPosition]
  )

  // Build label for the filter hint
  const filterHint = pendingConnection
    ? DATA_TYPE_INFO[pendingConnection.dataType]
    : null

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add Module"
      description="Search for a module to add to the pipeline"
    >
      <Command>
        <CommandInput ref={inputRef} placeholder="Search modules..." />
        {filterHint && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-border/30">
            <div
              className="w-2 h-2 rounded-full"
              style={{ background: filterHint.color }}
            />
            <span className="text-[11px] text-muted-foreground">
              Showing modules compatible with <span className="font-medium text-foreground">{filterHint.label.toLowerCase()}</span> type
            </span>
          </div>
        )}
        <CommandList>
          <CommandEmpty>No compatible modules found.</CommandEmpty>
          {CATEGORIES.map((category) => {
            // Get modules for this category, filtered if pending
            const modules = filteredModules
              ? filteredModules.filter((m) => m.category === category)
              : getModulesByCategory(category)
            if (modules.length === 0) return null
            return (
              <CommandGroup key={category} heading={CATEGORY_LABELS[category]}>
                {modules.map((mod) => {
                  const icon = MODULE_ICONS[mod.icon]
                  return (
                    <CommandItem
                      key={mod.module}
                      value={`${mod.module} ${mod.title}`}
                      onSelect={() => handleSelect(mod.module)}
                    >
                      {icon && (
                        <HugeiconsIcon
                          icon={icon}
                          size={16}
                          className="shrink-0 text-muted-foreground"
                        />
                      )}
                      <div className="flex flex-col gap-0.5">
                        <span className="text-sm">{mod.title}</span>
                        <span className="text-[11px] text-muted-foreground line-clamp-1">
                          {mod.description}
                        </span>
                      </div>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            )
          })}
        </CommandList>
      </Command>
    </CommandDialog>
  )
}
